import type { CSSProperties, ReactNode } from "react";

import { Box, Stack } from "@mantine/core";
import { useCallback, useEffect, useRef, useState } from "react";

import type { BeatCardState } from "../NarrativeBeatCard/NarrativeBeatCard";
import type { SendChannel } from "../SendLinkCard/SendLinkCard";
import type { BeatStatus, Narrative } from "../types";

import { NarrativeBeatCard } from "../NarrativeBeatCard/NarrativeBeatCard";
import { NarrativeCardActions } from "../NarrativeCardActions/NarrativeCardActions";
import { NarrativeEnrollWaiting } from "../NarrativeEnrollWaiting/NarrativeEnrollWaiting";
import { NarrativeGuideWaiting } from "../NarrativeGuideWaiting/NarrativeGuideWaiting";
import { NarrativeSendCard } from "../NarrativeSendCard/NarrativeSendCard";
import { buildNarrativeView } from "../narrativeView";
import { StepperBar } from "../StepperBar/StepperBar";
import styles from "./NarrativePanel.module.css";

/** Breathing room left above the active card when it is pinned to the top of the scroll region. */
const ACTIVE_TOP_OFFSET = 12;
/** Let the completion animation play out before the list scrolls to the next step. Kept short so the
 *  next script moves into place quickly, ready where the expert is already reading (Ricardo 08-27). */
const SCROLL_DELAY_MS = 300;
/** Eased scroll duration — snappier than the original 650ms while still smoother than native. */
const SCROLL_DURATION_MS = 440;

const easeInOut = (p: number): number => (p < 0.5 ? 2 * p * p : 1 - (-2 * p + 2) ** 2 / 2);

/** Ease `el`'s scrollTop to `to` over `SCROLL_DURATION_MS`; returns a cancel fn. */
const animateScroll = (el: HTMLElement, to: number): (() => void) => {
    let raf = 0;
    let startTime = 0;
    const from = el.scrollTop;
    const frame = (now: number) => {
        if (!startTime) startTime = now;
        const p = Math.min(1, (now - startTime) / SCROLL_DURATION_MS);
        el.scrollTop = from + (to - from) * easeInOut(p);
        if (p < 1) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => {
        if (raf) cancelAnimationFrame(raf);
    };
};

/** scrollTop that lands `card` at the top of the `body` scroll region (with breathing room). */
const topOffsetFor = (body: HTMLElement, card: HTMLElement): number =>
    body.scrollTop + (card.getBoundingClientRect().top - body.getBoundingClientRect().top) - ACTIVE_TOP_OFFSET;

export interface NarrativePanelProps {
    narrative: Narrative;
    /** Per-beat progress, keyed by beat id. Missing = pending. */
    statuses: Record<string, BeatStatus>;
    /** The beat currently being worked. */
    currentBeatId: string;
    /** Rendered between the stepper rail and the beat list, outside the scroll region. */
    notice?: ReactNode;
    /** Evidence cues (global snapshot); shown on Suggested cards only. */
    cues?: string[];
    onRead?: (beatId: string) => void;
    onSkip?: (beatId: string) => void;
    /** Fire the real guided-browsing send-link (Guide step form). Advancing is the host's job.
     *  Returning a promise lets the form show a loading state until the send resolves. */
    onGuideSend?: (beatId: string, channel: SendChannel, value: string) => void | Promise<void>;
    /** Fire the real enrollment send-link (Enroll step form). Advancing is the host's job. */
    onEnrollSend?: (beatId: string, channel: SendChannel, value: string) => void | Promise<void>;
    /** Disable the Guide/Enroll send buttons (e.g. an Expert University simulated task). */
    sendDisabled?: boolean;
    /** After the enrollment link is sent, hold the Enroll step in its "waiting to enroll" state. */
    awaitingEnrollment?: boolean;
    /** Return from the waiting state to the send form to send again. */
    onEnrollResend?: () => void;
    /** Disable re-send once the smart-link attempt cap is hit. */
    enrollResendDisabled?: boolean;
    /** After the guided-browsing link is sent, hold the Guide step until the expert continues. */
    awaitingGuide?: boolean;
    /** Advance past the held Guide step once the expert is done co-browsing. */
    onGuideContinue?: (beatId: string) => void;
    /** Return from the held Guide step to the send form to send again. */
    onGuideResend?: () => void;
    /** Re-open a skipped Guide step (from its card in the transcript) to send the link after all. */
    onGuideReopen?: (beatId: string) => void;
}

export const NarrativePanel = ({
    narrative,
    statuses,
    currentBeatId,
    notice,
    cues,
    onRead,
    onSkip,
    onGuideSend,
    onEnrollSend,
    sendDisabled,
    awaitingEnrollment,
    onEnrollResend,
    enrollResendDisabled,
    awaitingGuide,
    onGuideContinue,
    onGuideResend,
    onGuideReopen,
}: NarrativePanelProps) => {
    const view = buildNarrativeView(narrative, statuses, currentBeatId);

    // Keep the current step pinned to the top of the scroll region: whenever the cursor advances,
    // scroll the active card up to the top (history scrolls out of view above, still reachable).
    const bodyRef = useRef<HTMLDivElement>(null);
    const activeRef = useRef<HTMLDivElement>(null);

    // A viewport-sized tail below the content is what lets the active card actually reach the top:
    // only history + active + one preview render, so without it the scroll region can't scroll far
    // enough to lift a late beat all the way up. Sized to the scroll viewport and kept in sync.
    const [tailHeight, setTailHeight] = useState(0);

    useEffect(() => {
        const body = bodyRef.current;
        if (!body || typeof ResizeObserver === "undefined") return;
        const measure = () => setTailHeight(body.clientHeight);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(body);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const body = bodyRef.current;
        const active = activeRef.current;
        if (!body || !active) return;

        let cancel = () => {};
        // Hold briefly so the completion animation is visible, then ease the active card to the top.
        const timer = window.setTimeout(() => {
            cancel = animateScroll(body, topOffsetFor(body, active));
        }, SCROLL_DELAY_MS);

        return () => {
            clearTimeout(timer);
            cancel();
        };
    }, [currentBeatId, tailHeight]);

    // Jump to a beat from the "Sell" dropdown — scroll only, no change to the active step. Only beats
    // currently rendered (history + active + preview) are in the DOM; a not-yet-rendered future beat
    // simply has no anchor to scroll to, which is fine (the menu is used to go back).
    const scrollToBeat = useCallback((beatId: string) => {
        const body = bodyRef.current;
        const card = body?.querySelector<HTMLElement>(`[data-beat-id="${beatId}"]`);
        if (body && card) animateScroll(body, topOffsetFor(body, card));
    }, []);

    // Render one stable keyed list — history, active, and the next-beat preview. Keeping each beat
    // as the same element (keyed by id) means active → covered is a prop change that transitions in
    // place, not a remount, so the completion animation fires exactly once.
    const visible = view.ordered.slice(0, view.currentIndex + 2);

    return (
        <Stack gap={0} h="100%">
            <Box className={styles.stepper}>
                <StepperBar crumbs={view.crumbs} beats={view.menuBeats} onBeatSelect={scrollToBeat} />
            </Box>

            {/* Outside `.body`, so it stays put while the beat list scrolls. Vertical padding only —
                the child carries its own horizontal inset. */}
            {notice && <Box pt={12}>{notice}</Box>}

            <Stack ref={bodyRef} gap={18} p={16} className={styles.body}>
                {visible.map(({ beat, step }, i) => {
                    const isActive = i === view.currentIndex;
                    const status = statuses[beat.id];
                    // Status wins first — so the active beat animates into covered/skipped in place —
                    // then only the cursor beat may be "active"; any other still-pending beat greys out
                    // as a preview rather than rendering a second set of (dead) active controls.
                    const state: BeatCardState =
                        status === "covered"
                            ? "covered"
                            : status === "skipped"
                              ? "skipped"
                              : isActive
                                ? "active"
                                : "preview";

                    // Guide and Enroll steps pair the script beat with a send-link form card; while
                    // active, the form drives progression, so the beat's read/skip controls are hidden.
                    // Once the link is sent, each step swaps to a post-send waiting card: the Enroll step
                    // waits on the async enrollment-event; the Guide step holds until the expert continues.
                    const isEnrollWaiting = isActive && step.kind === "enroll" && !!awaitingEnrollment;
                    const isGuideWaiting = isActive && step.kind === "guide" && !!awaitingGuide;

                    let card: ReactNode;
                    if (isEnrollWaiting) {
                        card = (
                            <NarrativeEnrollWaiting onResend={onEnrollResend} resendDisabled={enrollResendDisabled} />
                        );
                    } else if (isGuideWaiting) {
                        card = (
                            <NarrativeGuideWaiting
                                onContinue={() => onGuideContinue?.(beat.id)}
                                onResend={onGuideResend}
                            />
                        );
                    } else {
                        let sendCard: ReactNode = null;
                        if (isActive && step.kind === "guide") {
                            sendCard = (
                                <NarrativeSendCard
                                    title="Send product details"
                                    cancelLabel="Skip"
                                    onCancel={() => onSkip?.(beat.id)}
                                    onSend={(channel, value) => onGuideSend?.(beat.id, channel, value)}
                                    sendDisabled={sendDisabled}
                                />
                            );
                        } else if (isActive && step.kind === "enroll") {
                            sendCard = (
                                <NarrativeSendCard
                                    title="Send enrollment"
                                    onSend={(channel, value) => onEnrollSend?.(beat.id, channel, value)}
                                    sendDisabled={sendDisabled}
                                />
                            );
                        }

                        const beatCard = (
                            <NarrativeBeatCard
                                beat={beat}
                                state={state}
                                cues={cues}
                                hideControls={!!sendCard}
                                onRead={isActive ? () => onRead?.(beat.id) : undefined}
                                onSkip={isActive ? () => onSkip?.(beat.id) : undefined}
                            />
                        );

                        // A skipped Guide step keeps a way back: its history card offers to send the
                        // co-browse link after all, which re-opens the step (see onGuideReopen).
                        const isSkippedGuide = !isActive && step.kind === "guide" && status === "skipped";

                        card = sendCard ? (
                            <Stack gap={14}>
                                {beatCard}
                                {sendCard}
                            </Stack>
                        ) : isSkippedGuide ? (
                            <>
                                {beatCard}
                                {/* Re-open is a secondary action → shared inline-link treatment (Ricardo 08-27). */}
                                <NarrativeCardActions>
                                    <NarrativeCardActions.Link onClick={() => onGuideReopen?.(beat.id)}>
                                        Send product details
                                    </NarrativeCardActions.Link>
                                </NarrativeCardActions>
                            </>
                        ) : (
                            beatCard
                        );
                    }

                    return isActive ? (
                        <div key={beat.id} data-beat-id={beat.id} ref={activeRef}>
                            {card}
                        </div>
                    ) : (
                        <div key={beat.id} data-beat-id={beat.id}>
                            {card}
                        </div>
                    );
                })}

                {/* Tail spacer — lets the active card scroll all the way to the top (see above). The
                    static flex lives in the CSS module; only the dynamic height rides in as a CSS
                    custom property (per CLAUDE.md's no-inline-styles policy). */}
                {tailHeight > 0 && (
                    <Box
                        aria-hidden
                        className={styles.tail}
                        style={{ "--lfa-tail-height": `${tailHeight}px` } as CSSProperties}
                    />
                )}
            </Stack>
        </Stack>
    );
};
